import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Battery, 
  Heart, 
  Moon, 
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Zap,
  ChevronRight,
  RefreshCw,
  Link2,
  Bell,
  BellOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function RecoveryInsightsWidget() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [premiumData, setPremiumData] = useState(null);
  const [recoveryAlertsEnabled, setRecoveryAlertsEnabled] = useState(true);

  useEffect(() => {
    fetchRecoveryData();
    fetchNotificationPrefs();
  }, [token]);

  const fetchRecoveryData = async () => {
    try {
      const [statusRes, dataRes] = await Promise.all([
        axios.get(`${API}/premium-wearables/all/status`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: null })),
        axios.get(`${API}/premium-wearables/all/data`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: null }))
      ]);
      
      setPremiumStatus(statusRes.data);
      setPremiumData(dataRes.data);
    } catch (error) {
      console.error('Error fetching recovery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      const response = await axios.get(`${API}/push/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveryAlertsEnabled(response.data.recovery_alerts_enabled !== false);
    } catch (error) {
      console.error('Error fetching notification prefs:', error);
    }
  };

  const toggleRecoveryAlerts = async () => {
    try {
      const newValue = !recoveryAlertsEnabled;
      await axios.put(`${API}/push/preferences`, {
        recovery_alerts_enabled: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveryAlertsEnabled(newValue);
      toast.success(newValue ? 'Recovery alerts enabled' : 'Recovery alerts disabled');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      // Sync all connected platforms
      const platforms = ['whoop', 'oura', 'apple-health'];
      for (const platform of platforms) {
        if (premiumStatus?.[platform]?.connected || premiumStatus?.[platform.replace('-', '_')]?.connected) {
          try {
            await axios.post(`${API}/premium-wearables/${platform}/sync`, null, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (e) {
            console.warn(`Failed to sync ${platform}:`, e);
          }
        }
      }
      await fetchRecoveryData();
      
      // Trigger recovery alert check after sync (if WHOOP or Oura connected)
      if (premiumStatus?.whoop?.connected || premiumStatus?.oura?.connected) {
        try {
          const alertRes = await axios.post(`${API}/push/send-recovery-alert`, null, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (alertRes.data.status === 'sent') {
            toast.info(`Recovery alert sent - your recovery is at ${alertRes.data.recovery_score}%`);
          }
        } catch (e) {
          // Silent fail - not critical
        }
      }
    } finally {
      setSyncing(false);
    }
  };

  // Check if any premium wearable is connected
  const hasConnectedDevice = premiumStatus && (
    premiumStatus.whoop?.connected ||
    premiumStatus.oura?.connected ||
    premiumStatus.apple_health?.connected ||
    premiumStatus.garmin?.connected
  );

  // Extract metrics from data
  const whoopData = premiumData?.platforms?.whoop;
  const ouraData = premiumData?.platforms?.oura;
  const appleData = premiumData?.platforms?.apple_health;

  // Calculate combined recovery score
  const recoveryScores = [];
  if (whoopData?.recovery?.recovery_score) recoveryScores.push(whoopData.recovery.recovery_score);
  if (ouraData?.readiness?.score) recoveryScores.push(ouraData.readiness.score);
  
  const avgRecovery = recoveryScores.length > 0 
    ? Math.round(recoveryScores.reduce((a, b) => a + b, 0) / recoveryScores.length)
    : null;

  // Get HRV (prefer WHOOP, then Oura, then Apple)
  const hrv = whoopData?.recovery?.hrv_rmssd_milli || 
              whoopData?.hrv ||
              ouraData?.sleep?.average_hrv ||
              appleData?.hrv?.average_hrv;

  // Get resting heart rate
  const restingHR = whoopData?.recovery?.resting_heart_rate ||
                    whoopData?.resting_hr ||
                    ouraData?.sleep?.lowest_heart_rate ||
                    appleData?.resting_heart_rate;

  // Get sleep score
  const sleepScore = ouraData?.sleep?.score || 
                     (whoopData?.sleep?.sleep_efficiency ? Math.round(whoopData.sleep.sleep_efficiency) : null);

  // Check if we have any data to show (even without recovery score)
  const hasAnyData = hrv || restingHR || sleepScore || appleData?.steps;

  // Determine training recommendation
  const getTrainingRecommendation = () => {
    if (!avgRecovery) return null;
    
    if (avgRecovery >= 67) {
      return {
        level: 'high',
        title: 'Peak Performance Day',
        message: 'Your recovery is excellent! Today is perfect for high-intensity training, PRs, or challenging workouts.',
        color: 'text-green-400',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30',
        icon: TrendingUp
      };
    } else if (avgRecovery >= 34) {
      return {
        level: 'moderate',
        title: 'Steady Training Day',
        message: 'Recovery is moderate. Stick to your regular training or consider a medium-intensity session.',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-500/10',
        borderColor: 'border-yellow-500/30',
        icon: Minus
      };
    } else {
      return {
        level: 'low',
        title: 'Recovery Focus Day',
        message: 'Your body needs rest. Focus on stretching, mobility work, or light active recovery.',
        color: 'text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30',
        icon: TrendingDown
      };
    }
  };

  const recommendation = getTrainingRecommendation();

  // Get recovery color
  const getRecoveryColor = (score) => {
    if (score >= 67) return 'text-green-400';
    if (score >= 34) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="card-tactical animate-pulse">
        <div className="h-6 bg-secondary/50 rounded w-1/3 mb-4"></div>
        <div className="h-24 bg-secondary/50 rounded"></div>
      </div>
    );
  }

  // If no device connected, show connect prompt
  if (!hasConnectedDevice) {
    return (
      <div 
        data-testid="recovery-widget-connect"
        className="card-tactical border border-dashed border-gray-600 hover:border-primary/50 transition-colors"
      >
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Battery className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-white font-semibold mb-2">Track Your Recovery</h3>
          <p className="text-gray-400 text-sm mb-4 max-w-xs">
            Connect WHOOP, Oura Ring, or Apple Watch to get personalized recovery insights and training recommendations.
          </p>
          <Button
            onClick={() => navigate('/wearables')}
            className="bg-primary hover:bg-red-700"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Connect Wearables
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="recovery-insights-widget"
      className={`card-tactical border ${recommendation?.borderColor || 'border-border'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Battery className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg text-white uppercase">Recovery Status</h3>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncing}
          className="text-gray-400 hover:text-white transition-colors p-1"
          title="Sync all devices"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Main Recovery Score */}
      {avgRecovery !== null && (
        <div className="flex items-center gap-6 mb-4">
          <div className="relative">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="#27272A"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke={avgRecovery >= 67 ? '#22C55E' : avgRecovery >= 34 ? '#EAB308' : '#EF4444'}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${(avgRecovery / 100) * 251} 251`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`font-mono text-2xl font-bold ${getRecoveryColor(avgRecovery)}`}>
                {avgRecovery}%
              </span>
              <span className="text-xs text-gray-500">Recovery</span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="flex-1 grid grid-cols-2 gap-3">
            {hrv && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-1">
                  <Heart className="w-3 h-3" />
                  HRV
                </div>
                <div className="text-white font-mono text-lg">
                  {Math.round(hrv)} <span className="text-xs text-gray-500">ms</span>
                </div>
              </div>
            )}
            {restingHR && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-1">
                  <Activity className="w-3 h-3" />
                  Resting HR
                </div>
                <div className="text-white font-mono text-lg">
                  {Math.round(restingHR)} <span className="text-xs text-gray-500">bpm</span>
                </div>
              </div>
            )}
            {sleepScore && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-1">
                  <Moon className="w-3 h-3" />
                  Sleep
                </div>
                <div className="text-white font-mono text-lg">
                  {sleepScore}
                </div>
              </div>
            )}
            {whoopData?.strain?.strain && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-1">
                  <Zap className="w-3 h-3" />
                  Strain
                </div>
                <div className="text-white font-mono text-lg">
                  {whoopData.strain.strain.toFixed(1)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Training Recommendation */}
      {recommendation && (
        <div className={`${recommendation.bgColor} ${recommendation.borderColor} border rounded-sm p-4`}>
          <div className="flex items-start gap-3">
            <recommendation.icon className={`w-5 h-5 ${recommendation.color} flex-shrink-0 mt-0.5`} />
            <div>
              <h4 className={`font-semibold ${recommendation.color}`}>{recommendation.title}</h4>
              <p className="text-sm text-gray-400 mt-1">{recommendation.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* No data state */}
      {avgRecovery === null && hasConnectedDevice && !hasAnyData && (
        <div className="text-center py-4">
          <AlertCircle className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No recovery data synced today</p>
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="text-primary text-sm hover:underline mt-2"
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      )}

      {/* Apple Health metrics without recovery score */}
      {avgRecovery === null && hasAnyData && (
        <div className="mb-4">
          <div className="text-center mb-3">
            <p className="text-gray-400 text-sm">Today's Health Metrics</p>
            <p className="text-xs text-gray-500">Connect WHOOP or Oura for recovery score</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {hrv && (
              <div className="bg-secondary/30 rounded-sm p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400 text-xs mb-1">
                  <Heart className="w-3 h-3" />
                  HRV
                </div>
                <div className="text-white font-mono text-xl">
                  {Math.round(hrv)}
                </div>
                <div className="text-xs text-gray-500">ms</div>
              </div>
            )}
            {restingHR && (
              <div className="bg-secondary/30 rounded-sm p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400 text-xs mb-1">
                  <Activity className="w-3 h-3" />
                  Resting HR
                </div>
                <div className="text-white font-mono text-xl">
                  {Math.round(restingHR)}
                </div>
                <div className="text-xs text-gray-500">bpm</div>
              </div>
            )}
            {appleData?.steps && (
              <div className="bg-secondary/30 rounded-sm p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400 text-xs mb-1">
                  <TrendingUp className="w-3 h-3" />
                  Steps
                </div>
                <div className="text-white font-mono text-xl">
                  {appleData.steps.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">today</div>
              </div>
            )}
            {appleData?.sleep?.total_hours && (
              <div className="bg-secondary/30 rounded-sm p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-gray-400 text-xs mb-1">
                  <Moon className="w-3 h-3" />
                  Sleep
                </div>
                <div className="text-white font-mono text-xl">
                  {appleData.sleep.total_hours.toFixed(1)}
                </div>
                <div className="text-xs text-gray-500">hours</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Sources */}
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          {premiumStatus?.whoop?.connected && (
            <span className="text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-400">WHOOP</span>
          )}
          {premiumStatus?.oura?.connected && (
            <span className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">Oura</span>
          )}
          {premiumStatus?.apple_health?.connected && (
            <span className="text-xs px-2 py-1 rounded-full bg-pink-500/10 text-pink-400">Apple</span>
          )}
        </div>
        <button
          onClick={() => navigate('/wearables')}
          className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
        >
          Manage Devices
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Recovery Alerts Toggle */}
      {(premiumStatus?.whoop?.connected || premiumStatus?.oura?.connected) && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {recoveryAlertsEnabled ? (
                <Bell className="w-4 h-4 text-green-500" />
              ) : (
                <BellOff className="w-4 h-4 text-gray-500" />
              )}
              <span className="text-sm text-gray-400">
                {recoveryAlertsEnabled ? 'Low recovery alerts enabled' : 'Recovery alerts disabled'}
              </span>
            </div>
            <button
              onClick={toggleRecoveryAlerts}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                recoveryAlertsEnabled
                  ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                  : 'bg-primary/10 text-primary hover:bg-primary/20'
              }`}
            >
              {recoveryAlertsEnabled ? 'Disable' : 'Enable'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Get notified when your recovery drops below 34%
          </p>
        </div>
      )}
    </div>
  );
}
